// src/pages/UserSettingsPage.js
//
// UserSettingsPage — Enterprise Layout (Sticky Tabs inside Yellow Scroll)
// ======================================================================
//
// What’s improved vs your current version:
// 1) Keeps your Commission editor unchanged (logic preserved).
// 2) Adds "enterprise" internal navigation:
//    - Sticky tabs inside the yellow scroll container
//    - Smooth scrolling with correct offset (tabs won't cover section headings)
//    - Active tab tracking based on scroll position
// 3) Replaces "Vacation placeholder" with a fully editable VacationSettingsSection.
//
// Architecture:
// - Page loads:
//    * /user-settings/:userId   (commission settings, tiers)
//    * /auth/users/admin/:id    (user details)
// - Vacation section loads itself:
//    * /vacation-settings/:userId (bundle)
//    * /vacation-settings/:userId/profile (save)
//    * /vacation-settings/:userId/work-schedule (save)
//
// i18n note:
// - Backend/database field names stay unchanged: commission_service,
//   commission_product, commission_mode, tier1_requires_reach, etc.
// - Translation is intentionally limited to frontend-owned UI text, validation
//   messages and local success/error banners. API payload keys and enum values
//   remain stable because the backend contract and existing migrations depend
//   on them.
// - VacationSettingsSection is a separate component and is not translated here;
//   it should be handled in its own focused i18n batch so its leave/vacation
//   business rules are not accidentally mixed with the commission editor.
//

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  fetchUserSettings,
  updateUserSettingsField,
  fetchUserByIdAdmin,
  updateCommissionPlan,
} from "../services/userService";

import VacationSettingsSection from "../components/userSettings/VacationSettingsSection";

import "./UserSettingsPage.css";

const UserSettingsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "userSettings"]);

  // Commission settings (existing)
  const [settings, setSettings] = useState(null);
  const [tiers, setTiers] = useState([]);

  // User header info
  const [currentUser, setCurrentUser] = useState(null);

  // Page-level banners
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Commission-level loading state (kept as you had)
  const [isLoading, setIsLoading] = useState(false);

  /* ---------------------------------------------------------------------- */
  /* Sticky tabs infra                                                       */
  /* ---------------------------------------------------------------------- */
  const scrollRef = useRef(null);      // the yellow scroll container
  const tabsRef = useRef(null);        // sticky tabs bar
  const commissionRef = useRef(null);  // section anchor
  const vacationRef = useRef(null);    // section anchor

  const [activeTab, setActiveTab] = useState("commission"); // "commission" | "vacation"

  /**
   * Scroll within the yellow container to a target section.
   * We compute an offset to account for the sticky tabs height.
   */
  const scrollToSection = (sectionRef, tabKey) => {
    const scroller = scrollRef.current;
    const el = sectionRef.current;
    if (!scroller || !el) return;

    // Height of sticky tab bar (so heading isn't hidden behind it)
    const tabsH = tabsRef.current?.offsetHeight || 0;

    // offsetTop is relative to the scroll container content
    const top = Math.max(0, el.offsetTop - tabsH - 10);

    scroller.scrollTo({ top, behavior: "smooth" });
    setActiveTab(tabKey);
  };

  /**
   * Active tab tracking:
   * We compute which section is currently "closest" to the top
   * of the scroll container (minus sticky tabs height).
   *
   * This gives the “enterprise feel” (tab highlights follow scroll).
   */
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    let raf = 0;

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;

        const tabsH = tabsRef.current?.offsetHeight || 0;
        const y = scroller.scrollTop + tabsH + 12;

        const cTop = commissionRef.current?.offsetTop ?? 0;
        const vTop = vacationRef.current?.offsetTop ?? Number.POSITIVE_INFINITY;

        // If we're below vacation anchor => vacation active
        if (y >= vTop) setActiveTab("vacation");
        else setActiveTab("commission");
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);

  /**
   * Convert backend setting field names into localized, user-friendly labels.
   *
   * Important: the returned label is display-only. The original backend field
   * name is still sent to updateUserSettingsField exactly as before.
   */
  const getCommissionFieldLabel = (field) =>
    t(`userSettings:commission.fields.${field}`, {
      defaultValue: String(field || "").replace("_", " "),
    });

  /**
   * Convert backend commission mode codes into display labels.
   *
   * LINEAR/TIERED remain technical codes in API calls and state. Only the text
   * shown in banners/buttons is localized.
   */
  const getCommissionModeLabel = (mode) =>
    t(`userSettings:commission.modes.${String(mode || "").toLowerCase()}`, {
      defaultValue: String(mode || ""),
    });

  /* ------------------------------------------------------------------------ */
  /* Load data                                                                */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await fetchUserSettings(userId);
        setSettings(settingsData);

        // If backend includes tiers, normalize them for editable inputs.
        const incoming = Array.isArray(settingsData.commission_tiers)
          ? settingsData.commission_tiers
          : [];

        setTiers(
          incoming.map((tier) => ({
            id: tier.id,
            min_amount: String(tier.min_amount ?? "0.00"),
            max_amount: tier.max_amount === null || tier.max_amount === undefined ? "" : String(tier.max_amount),
            service_rate: String(tier.service_rate ?? "0.00"),
            product_rate: String(tier.product_rate ?? "0.00"),
          }))
        );
      } catch (err) {
        console.error(`[UserSettingsPage] ❌ Failed to load settings for user ${userId}:`, err);
        setError(t("userSettings:messages.loadSettingsFailed"));
      }
    };

    const loadUser = async () => {
      try {
        const user = await fetchUserByIdAdmin(userId);
        setCurrentUser(user);
      } catch (err) {
        console.error(`[UserSettingsPage] ❌ Failed to load user ${userId}:`, err);
        setError(t("userSettings:messages.loadUserFailed"));
      }
    };

    loadSettings();
    loadUser();
  }, [t, userId]);

  const commissionMode = settings?.commission_mode || "LINEAR";
  const tier1RequiresReach = !!settings?.tier1_requires_reach;

  /* ------------------------------------------------------------------------ */
  /* Linear updates (existing behavior)                                       */
  /* ------------------------------------------------------------------------ */
  const handleFieldUpdate = async (field, value) => {
    let formattedValue = parseFloat(value);
    const fieldLabel = getCommissionFieldLabel(field);

    if (isNaN(formattedValue) || formattedValue < 0 || formattedValue > 99.99) {
      setError(t("userSettings:messages.percentRange", { field: fieldLabel }));
      setSuccessMessage("");
      return;
    }

    formattedValue = formattedValue.toFixed(2);

    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await updateUserSettingsField(userId, field, formattedValue);
      setSettings((prev) => ({ ...prev, [field]: formattedValue }));
      setSuccessMessage(t("userSettings:messages.fieldUpdated", { field: fieldLabel }));
    } catch (err) {
      console.error(`[UserSettingsPage] ❌ Failed to update ${field}:`, err);
      setError(t("userSettings:messages.fieldUpdateFailed", { field: fieldLabel }));
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Mode switching                                                           */
  /* ------------------------------------------------------------------------ */
  const onModeChange = async (newMode) => {
    const mode = String(newMode || "").toUpperCase();
    if (!["LINEAR", "TIERED"].includes(mode)) return;

    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await updateUserSettingsField(userId, "commission_mode", mode);
      setSettings((prev) => ({ ...prev, commission_mode: mode }));

      // If switching to TIERED and no tiers exist yet, create a default tier.
      if (mode === "TIERED" && (!tiers || tiers.length === 0)) {
        const cs = String(settings?.commission_service ?? "0.00");
        const cp = String(settings?.commission_product ?? "0.00");
        setTiers([{ min_amount: "0.00", max_amount: "", service_rate: cs, product_rate: cp }]);
      }

      setSuccessMessage(t("userSettings:messages.commissionModeUpdated", { mode: getCommissionModeLabel(mode) }));
    } catch (err) {
      console.error(`[UserSettingsPage] ❌ Failed to update commission_mode:`, err);
      setError(t("userSettings:messages.commissionModeUpdateFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Tier #1 rule toggle                                                      */
  /* ------------------------------------------------------------------------ */
  const onToggleTier1RequiresReach = async (checked) => {
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await updateUserSettingsField(userId, "tier1_requires_reach", !!checked);
      setSettings((prev) => ({ ...prev, tier1_requires_reach: !!checked }));
      setSuccessMessage(
        checked
          ? t("userSettings:messages.tier1RequiresReachEnabled")
          : t("userSettings:messages.tier1RequiresReachDisabled")
      );
    } catch (err) {
      console.error("[UserSettingsPage] ❌ Failed to update tier1_requires_reach:", err);
      setError(t("userSettings:messages.tier1RequiresReachUpdateFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Tier validation                                                          */
  /* ------------------------------------------------------------------------ */
  const tierValidationError = useMemo(() => {
    if (commissionMode !== "TIERED") return "";
    if (!Array.isArray(tiers) || tiers.length === 0) return t("userSettings:messages.tieredRequiresAtLeastOneTier");

    const firstMin = Number(tiers[0]?.min_amount);
    if (!Number.isFinite(firstMin) || firstMin !== 0) return t("userSettings:messages.firstTierMustStartAtZero");

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const tierNo = i + 1;
      const min = Number(tier.min_amount);
      const max = tier.max_amount === "" ? null : Number(tier.max_amount);

      if (!Number.isFinite(min) || min < 0) return t("userSettings:messages.tierMinInvalid", { tier: tierNo });
      if (max !== null && (!Number.isFinite(max) || max <= min)) {
        return t("userSettings:messages.tierMaxInvalid", { tier: tierNo });
      }
      if (i < tiers.length - 1 && max === null) {
        return t("userSettings:messages.onlyLastTierCanBeInfinity", { tier: tierNo });
      }

      const serviceRate = Number(tier.service_rate);
      const productRate = Number(tier.product_rate);
      if (!Number.isFinite(serviceRate) || serviceRate < 0 || serviceRate > 99.99) {
        return t("userSettings:messages.tierServiceRateInvalid", { tier: tierNo });
      }
      if (!Number.isFinite(productRate) || productRate < 0 || productRate > 99.99) {
        return t("userSettings:messages.tierProductRateInvalid", { tier: tierNo });
      }

      // Contiguity: next.min === current.max
      if (i < tiers.length - 1) {
        const nextMin = Number(tiers[i + 1].min_amount);
        if (max === null) return t("userSettings:messages.tierMaxCannotBeInfinityBeforeLast", { tier: tierNo });
        if (nextMin !== max) return t("userSettings:messages.tiersMustBeContiguous", { tier: tierNo });
      }
    }

    // If rule enabled: tier #1 must have a finite max threshold.
    if (tier1RequiresReach) {
      const max0 = tiers[0]?.max_amount;
      if (max0 === "" || max0 == null) {
        return t("userSettings:messages.tier1RuleNoMax");
      }
      const maxVal = Number(max0);
      if (!Number.isFinite(maxVal) || maxVal <= 0) {
        return t("userSettings:messages.tier1ThresholdInvalid");
      }
    }

    return "";
  }, [commissionMode, tier1RequiresReach, tiers, t]);

  /* ------------------------------------------------------------------------ */
  /* Tier editor helpers                                                      */
  /* ------------------------------------------------------------------------ */
  const setTierField = (index, field, value) => {
    setTiers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };

      // If user changes max, auto-set next.min to keep contiguity UX-friendly.
      if (field === "max_amount" && index < next.length - 1) {
        const max = value === "" ? "" : String(value);
        next[index + 1] = { ...next[index + 1], min_amount: max || next[index + 1].min_amount };
      }

      return next;
    });
  };

  const addTier = () => {
    setTiers((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];

      if (list.length === 0) {
        return [{ min_amount: "0.00", max_amount: "", service_rate: "0.00", product_rate: "0.00" }];
      }

      const last = list[list.length - 1];
      const lastMin = Number(last.min_amount);

      // If last tier was infinity, suggest a max so user can split it.
      const suggestedMax = (Number.isFinite(lastMin) ? lastMin + 1000 : 1000).toFixed(2);

      if (last.max_amount === "" || last.max_amount === null || last.max_amount === undefined) {
        last.max_amount = suggestedMax;
      }

      const newMin = String(last.max_amount);
      list[list.length - 1] = { ...last };

      list.push({
        min_amount: newMin,
        max_amount: "",
        service_rate: String(last.service_rate ?? "0.00"),
        product_rate: String(last.product_rate ?? "0.00"),
      });

      return list;
    });
  };

  const deleteTier = (index) => {
    setTiers((prev) => {
      const list = [...prev];
      list.splice(index, 1);

      // Restitch contiguity
      for (let i = 0; i < list.length - 1; i++) {
        if (list[i].max_amount !== "") list[i + 1].min_amount = String(list[i].max_amount);
      }

      if (list.length > 0) list[0].min_amount = "0.00";
      return list;
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Save tier plan atomically                                                */
  /* ------------------------------------------------------------------------ */
  const saveTierPlan = async () => {
    setError("");
    setSuccessMessage("");

    if (tierValidationError) {
      setError(tierValidationError);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        mode: "TIERED",
        tiers: tiers.map((tier) => ({
          min_amount: tier.min_amount,
          max_amount: tier.max_amount === "" ? null : tier.max_amount,
          service_rate: tier.service_rate,
          product_rate: tier.product_rate,
        })),
      };

      const res = await updateCommissionPlan(userId, payload);

      setSettings((prev) => ({ ...prev, commission_mode: "TIERED" }));

      const fresh = Array.isArray(res?.commission_tiers) ? res.commission_tiers : [];
      setTiers(
        fresh.map((tier) => ({
          id: tier.id,
          min_amount: String(tier.min_amount ?? "0.00"),
          max_amount: tier.max_amount === null || tier.max_amount === undefined ? "" : String(tier.max_amount),
          service_rate: String(tier.service_rate ?? "0.00"),
          product_rate: String(tier.product_rate ?? "0.00"),
        }))
      );

      setSuccessMessage(t("userSettings:messages.tierPlanSaved"));
    } catch (err) {
      console.error("[UserSettingsPage] ❌ Failed to save tier plan:", err);
      setError(err?.response?.data?.message || t("userSettings:messages.tierPlanSaveFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */
  return (
    <div className="user-settings-container">
      {/* Top bar: title + subtle back link (static; never scrolls away) */}
      <div className="user-settings-topbar">
        <h1 className="user-settings-header">{t("userSettings:title")}</h1>
        <button className="back-link" type="button" onClick={() => navigate("/users")} disabled={isLoading}>
          {t("userSettings:actions.backToUsers")}
        </button>
      </div>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {currentUser ? (
        <div className="us-user-info">
          <div className="us-user-info-header">
            <h2>{t("userSettings:userDetails.title")}</h2>
          </div>

          <div className="us-user-details-grid">
            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.id")}</span>
              <span className="us-value">{currentUser.id}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.username")}</span>
              <span className="us-value">{currentUser.username}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.firstName")}</span>
              <span className="us-value">{currentUser.first_name}</span>
            </div>

            <div className="us-field">
              <span className="us-label">{t("userSettings:userDetails.lastName")}</span>
              <span className="us-value">{currentUser.last_name}</span>
            </div>
          </div>
        </div>
      ) : (
        <p>{t("userSettings:states.loadingUserInformation")}</p>
      )}

      {settings ? (
        <div className="settings-block">
          {/* Yellow scroll viewport */}
          <div className="settings-scroll" ref={scrollRef}>
            {/* Sticky Tabs inside yellow scroll */}
            <div className="settings-sticky-tabs" ref={tabsRef}>
              <button
                type="button"
                className={activeTab === "commission" ? "tab-btn active" : "tab-btn"}
                onClick={() => scrollToSection(commissionRef, "commission")}
                disabled={isLoading}
              >
                {t("userSettings:tabs.commission")}
              </button>

              <button
                type="button"
                className={activeTab === "vacation" ? "tab-btn active" : "tab-btn"}
                onClick={() => scrollToSection(vacationRef, "vacation")}
                disabled={isLoading}
              >
                {t("userSettings:tabs.vacation")}
              </button>
            </div>

            {/* ============================= */}
            {/* COMMISSION SECTION            */}
            {/* ============================= */}
            <div ref={commissionRef} className="section-anchor">
              <div className="section-card">
                <div className="section-header">
                  <h2>{t("userSettings:commission.title")}</h2>
                  <p className="section-subtitle">
                    {t("userSettings:commission.subtitle")}
                  </p>
                </div>

                {/* MODE SWITCH */}
                <div className="commission-mode-box">
                  <label className="commission-mode-label">{t("userSettings:commission.modeLabel")}</label>

                  <div className="commission-mode-buttons">
                    <button
                      type="button"
                      className={commissionMode === "LINEAR" ? "mode-btn active" : "mode-btn"}
                      onClick={() => onModeChange("LINEAR")}
                      disabled={isLoading}
                    >
                      {t("userSettings:commission.modes.linear")}
                    </button>

                    <button
                      type="button"
                      className={commissionMode === "TIERED" ? "mode-btn active" : "mode-btn"}
                      onClick={() => onModeChange("TIERED")}
                      disabled={isLoading}
                    >
                      {t("userSettings:commission.modes.tiered")}
                    </button>
                  </div>

                  <p className="commission-mode-hint">
                    {t("userSettings:commission.modeHint")}
                  </p>
                </div>

                {/* LINEAR UI */}
                {commissionMode === "LINEAR" && (
                  <div className="commission-box">
                    <div className="commission-field">
                      <label>{t("userSettings:commission.fields.commission_service")}</label>
                      <input
                        type="number"
                        step="0.01"
                        max="99.99"
                        min="0"
                        value={settings.commission_service || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (parseFloat(value) <= 99.99) setSettings((prev) => ({ ...prev, commission_service: value }));
                        }}
                        onBlur={(e) => handleFieldUpdate("commission_service", e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    <div className="commission-field">
                      <label>{t("userSettings:commission.fields.commission_product")}</label>
                      <input
                        type="number"
                        step="0.01"
                        max="99.99"
                        min="0"
                        value={settings.commission_product || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (parseFloat(value) <= 99.99) setSettings((prev) => ({ ...prev, commission_product: value }));
                        }}
                        onBlur={(e) => handleFieldUpdate("commission_product", e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                )}

                {/* TIERED UI */}
                {commissionMode === "TIERED" && (
                  <div className="tiers-block">
                    <h3>{t("userSettings:tiers.title")}</h3>

                    <div className="tier1-rule-box">
                      <label className="tier1-rule-label">
                        <input
                          type="checkbox"
                          checked={tier1RequiresReach}
                          disabled={isLoading}
                          onChange={(e) => onToggleTier1RequiresReach(e.target.checked)}
                        />
                        <span>{t("userSettings:tiers.tier1RuleLabel")}</span>
                      </label>

                      <p className="tier1-rule-hint">
                        {t("userSettings:tiers.tier1RuleHint")}
                      </p>
                    </div>

                    <div className="tiers-actions">
                      <button type="button" className="mode-btn add-tier-btn" onClick={addTier} disabled={isLoading}>
                        {t("userSettings:actions.addTier")}
                      </button>

                      <button type="button" className="primary" onClick={saveTierPlan} disabled={isLoading}>
                        {t("userSettings:actions.saveTierPlan")}
                      </button>
                    </div>

                    {tierValidationError && <p className="error-message">{tierValidationError}</p>}

                    <div className="tiers-table-wrapper">
                      <div className="tiers-scroll">
                        <table className="tiers-table">
                          <thead>
                            <tr>
                              <th>{t("userSettings:tiers.table.from")}</th>
                              <th>{t("userSettings:tiers.table.to")}</th>
                              <th>{t("userSettings:tiers.table.servicePercent")}</th>
                              <th>{t("userSettings:tiers.table.productPercent")}</th>
                              <th></th>
                            </tr>
                          </thead>

                          <tbody>
                            {tiers.map((tier, idx) => {
                              const isLast = idx === tiers.length - 1;

                              return (
                                <tr key={tier.id || `tier-${idx}`}>
                                  <td>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={tier.min_amount}
                                      disabled={idx === 0 || isLoading}
                                      onChange={(e) => setTierField(idx, "min_amount", e.target.value)}
                                    />
                                  </td>

                                  <td>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={tier.max_amount}
                                      placeholder={isLast ? "∞" : ""}
                                      disabled={isLast || isLoading}
                                      onChange={(e) => setTierField(idx, "max_amount", e.target.value)}
                                    />
                                  </td>

                                  <td>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="99.99"
                                      value={tier.service_rate}
                                      disabled={isLoading}
                                      onChange={(e) => setTierField(idx, "service_rate", e.target.value)}
                                    />
                                  </td>

                                  <td>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="99.99"
                                      value={tier.product_rate}
                                      disabled={isLoading}
                                      onChange={(e) => setTierField(idx, "product_rate", e.target.value)}
                                    />
                                  </td>

                                  <td>
                                    <button
                                      type="button"
                                      className="danger"
                                      onClick={() => deleteTier(idx)}
                                      disabled={isLoading || tiers.length <= 1}
                                      title={t("userSettings:actions.deleteTierTitle")}
                                    >
                                      {t("common:actions.delete")}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        <div className="tiers-scroll-spacer" />
                      </div>
                    </div>

                    <p className="tiers-hint">
                      {t("userSettings:tiers.rulesHint")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ============================= */}
            {/* VACATION SECTION (EDITABLE)   */}
            {/* ============================= */}
            <div ref={vacationRef} className="section-anchor">
              <div className="section-card">
                <VacationSettingsSection userId={userId} />
              </div>
            </div>

            {/* Spacer so the last card can fully scroll into view */}
            <div className="settings-scroll-end-spacer" />
          </div>
        </div>
      ) : (
        <p>{t("userSettings:states.loadingSettings")}</p>
      )}
    </div>
  );
};

export default UserSettingsPage;
